import { createCryptoCurrency } from '@0xtorch/core'
import { telos as viemTelos } from 'viem/chains'
import { type Client, createClient } from '../../client'
import type { Explorer } from '../../explorer'
import { createBlockscout } from '../../explorer'
import type { LowerHex } from '../../types'
import type { Chain } from '../types/chain'
import type { HttpRpc } from '../types/rpc'

/**
 * Telos のネイティブトークンとして利用する TLOS 通貨定義。
 */
const telos = createCryptoCurrency({
  type: 'CryptoCurrency',
  id: 'telos',
  name: 'Telos',
  symbol: 'TLOS',
  market: {
    coingeckoId: 'telos',
  },
  updatedAt: 0,
})

/**
 * Telos チェーン定義を作成する。
 *
 * @param params.explorerProxyUrl - Explorer API を中継するプロキシ URL
 * @returns Telos の EVM チェーン定義
 */
export const createTelosChain = ({
  explorerProxyUrl: proxyUrl,
}: {
  explorerProxyUrl?: string
}): Chain =>
  createTelosChainCustom({
    client: createClient({
      chain: viemTelos,
      httpRpcs: telosHttpRpcs,
    }),
    explorer: createBlockscout({
      name: 'Teloscan',
      baseUrl: 'https://teloscan.io',
      apiBaseUrl: 'https://teloscan.io/api',
      proxyUrl,
      useAddressInternalTransactionsV2: true,
    }),
  })

type CreateTelosChainParameters = {
  client: Client
  explorer: Explorer
}

/**
 * Telos チェーン定義を任意の client / explorer で作成する。
 *
 * @param params.client - EVM RPC クライアント
 * @param params.explorer - Explorer クライアント
 * @returns Telos の EVM チェーン定義
 */
export const createTelosChainCustom = ({
  client,
  explorer,
}: CreateTelosChainParameters): Chain => ({
  id: 40,
  network: 'telos',
  name: 'Telos',
  nativeToken: {
    name: 'Telos',
    symbol: 'TLOS',
    decimals: 18,
    currency: telos,
  },
  wrappedTokenAddresses: new Set<LowerHex>([
    '0xd102ce6a4db07d247fcc28f366a623df0938ca9e',
  ]),
  explorer,
  client,
  blockTime: 500,
  coingeckoId: 'telos',
  defillamaId: 'telos',
})

/**
 * Telos の HTTP RPC 候補。
 */
export const telosHttpRpcs: HttpRpc[] = [
  {
    url: 'https://rpc.telos.net',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  {
    url: 'https://telos.drpc.org',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
]

/**
 * Telos の WebSocket RPC 候補。
 */
export const telosWebsocketRpcUrls: readonly string[] = []
